import React, { useState, useEffect, useRef } from "react";

import { EdiClaimLine } from "../types";
import { formatDrgFromFields } from "../drgDisplay";
import { formatDisplayDate } from "../../../helpers/dataHelper";
import {
  formatCurrency,
  formatEntityName,
  getModifiersFromLine,
} from "../viewerHelpers";
import DetailSection from "./DetailSection";
import HoverTooltip from "../../../ui/HoverTooltip";

const ClaimServiceLineRow: React.FC<{
  line: EdiClaimLine;
  index: number;
  allExpanded?: boolean;
  showModifiersColumn?: boolean;
  showRevNdcColumn?: boolean;
}> = ({
  line,
  index,
  allExpanded,
  showModifiersColumn = true,
  showRevNdcColumn = true,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showCodeTooltip, setShowCodeTooltip] = useState(false);
  const codeRef = useRef<HTMLSpanElement>(null);

  // Sync with allExpanded prop
  useEffect(() => {
    if (allExpanded !== undefined) {
      setExpanded(allExpanded);
    }
  }, [allExpanded]);
  const hasServiceDateTo =
    line.serviceDateTo && line.serviceDateTo !== line.serviceDateFrom;
  const normalizedClaimUnitType = (line.unitType || "").trim().toUpperCase();
  const hasUnitType = !!(
    normalizedClaimUnitType &&
    normalizedClaimUnitType !== "UNIT" &&
    normalizedClaimUnitType !== "UN"
  );
  const lineModifiers = getModifiersFromLine(line);
  const hasModifiers = lineModifiers.length > 0;
  const hasDiagnosisPointers =
    line.diagnosisPointers && line.diagnosisPointers.length > 0;
  const hasProviders = line.providers && line.providers.length > 0;
  const hasDrug = line.drug;
  const lineDrg = formatDrgFromFields(line.drg, line.drgCode);
  const hasLineDrg = !!lineDrg;

  const hasDetails =
    hasServiceDateTo ||
    hasUnitType ||
    hasModifiers ||
    hasDiagnosisPointers ||
    hasProviders ||
    hasDrug ||
    hasLineDrg;
  const hasExpandableContent =
    hasServiceDateTo ||
    hasUnitType ||
    hasModifiers ||
    hasDiagnosisPointers ||
    hasProviders ||
    hasDrug ||
    hasLineDrg;

  return (
    <>
      <tr
        className={`border-b border-gray-100 hover:bg-gray-50 ${hasExpandableContent ? "cursor-pointer" : ""}`}
        onClick={() => hasExpandableContent && setExpanded(!expanded)}
        role={hasExpandableContent ? "button" : undefined}
        aria-expanded={hasExpandableContent ? expanded : undefined}
        tabIndex={hasExpandableContent ? 0 : undefined}
      >
        <td className="px-3 py-2 text-sm">
          {hasExpandableContent && (
            <span
              className={`inline-block transition-transform ${expanded ? "rotate-90" : ""}`}
            >
              ▶
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-md font-medium text-gray-900">
          {index + 1}
        </td>
        <td className="px-3 py-2">
          <div className="text-md font-medium text-gray-900 cursor-default">
            <span
              ref={codeRef}
              onMouseEnter={() => setShowCodeTooltip(true)}
              onMouseLeave={() => setShowCodeTooltip(false)}
            >
              {line.procedure?.code || line.revenueCode?.code || "-"}
            </span>
            <HoverTooltip
              title={line.procedure?.desc || line.revenueCode?.desc || ""}
              isVisible={showCodeTooltip}
              triggerRef={codeRef}
            />
          </div>
        </td>
        {showModifiersColumn && (
          <td className="px-3 py-2">
            <div className="flex flex-wrap gap-1">
              {lineModifiers.map((mod, i) => (
                <span
                  key={i}
                  className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-sm font-mono"
                >
                  {mod}
                </span>
              ))}
              {lineModifiers.length === 0 && (
                <span className="text-gray-400">-</span>
              )}
            </div>
          </td>
        )}
        <td className="px-3 py-2 text-md text-gray-600 text-right">
          {line.unitCount ?? 1}
        </td>
        <td className="px-3 py-2 text-md text-gray-900 text-right font-medium">
          {formatCurrency(line.chargeAmount)}
        </td>
        {showRevNdcColumn && (
          <td className="px-3 py-2 text-md text-gray-600">
            <div className="flex flex-col gap-0.5">
              {line.revenueCode?.code && (
                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded text-sm w-fit">
                  Rev: {line.revenueCode.code}
                </span>
              )}
              {line.drug?.code && (
                <span
                  className="px-1.5 py-0.5 bg-teal-100 text-teal-800 rounded text-sm w-fit"
                  title={line.drug.desc || ""}
                >
                  NDC: {line.drug.formattedCode || line.drug.code}
                </span>
              )}
              {!line.revenueCode?.code && !line.drug?.code && (
                <span className="text-gray-400">-</span>
              )}
            </div>
          </td>
        )}
      </tr>
      {expanded && hasDetails && (
        <tr className="bg-gray-50">
          <td
            colSpan={
              5 + (showModifiersColumn ? 1 : 0) + (showRevNdcColumn ? 1 : 0)
            }
            className="px-4 py-2"
          >
            <div className="space-y-2">
              {/* Service Dates */}
              {hasServiceDateTo && (
                <DetailSection title="Dates">
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">From:</span>{" "}
                    {formatDisplayDate(line.serviceDateFrom)} |
                    <span className="font-medium ml-2">To:</span>{" "}
                    {formatDisplayDate(line.serviceDateTo)}
                  </div>
                </DetailSection>
              )}

              {/* Unit Information */}
              {hasUnitType && (
                <DetailSection title="Units">
                  <div className="text-sm text-gray-700">
                    <span className="text-gray-600">Type:</span>{" "}
                    <span className="font-medium">{line.unitType}</span>
                  </div>
                </DetailSection>
              )}

              {/* Modifiers */}
              {hasModifiers && (
                <DetailSection title="MOD">
                  <div className="flex flex-wrap gap-1">
                    {lineModifiers.map((modifier, i) => (
                      <span
                        key={i}
                        className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-sm font-mono"
                      >
                        {modifier}
                      </span>
                    ))}
                  </div>
                </DetailSection>
              )}

              {/* Diagnosis Pointers */}
              {hasDiagnosisPointers && (
                <DetailSection title="Dx Ptrs">
                  <div className="flex flex-wrap gap-1">
                    {line.diagnosisPointers?.map((pointer, i) => (
                      <span
                        key={i}
                        className="px-1.5 py-0.5 bg-green-100 text-green-800 rounded text-sm font-mono"
                      >
                        {pointer}
                      </span>
                    ))}
                  </div>
                </DetailSection>
              )}

              {/* Drug Information (NDC) */}
              {hasDrug && (
                <DetailSection title="Drug">
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <div>
                      <span className="text-gray-600">NDC:</span>{" "}
                      <span className="font-mono font-medium">
                        {line.drug?.formattedCode || line.drug?.code || "-"}
                      </span>
                    </div>
                    {line.drug?.desc && (
                      <div>
                        <span className="text-gray-600">Name:</span>{" "}
                        <span className="font-medium">{line.drug.desc}</span>
                      </div>
                    )}
                    {line.drugQuantity !== undefined && (
                      <div>
                        <span className="text-gray-600">Qty:</span>{" "}
                        <span className="font-medium">{line.drugQuantity}</span>
                      </div>
                    )}
                    {line.drugUnitType && (
                      <div>
                        <span className="text-gray-600">Unit:</span>{" "}
                        <span className="font-medium">{line.drugUnitType}</span>
                      </div>
                    )}
                  </div>
                </DetailSection>
              )}

              {/* Providers */}
              {hasProviders && (
                <DetailSection title="Providers">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] text-gray-500 uppercase">
                        <th className="text-left px-2 py-0.5">Role</th>
                        <th className="text-left px-2 py-0.5">Type</th>
                        <th className="text-left px-2 py-0.5">ID</th>
                        <th className="text-left px-2 py-0.5">Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {line.providers?.map((provider, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-0.5 text-gray-600 text-md">
                            {provider.entityRole || "-"}
                          </td>
                          <td className="px-2 py-0.5 text-gray-600 text-md">
                            {provider.entityType || "-"}
                          </td>
                          <td className="px-2 py-0.5 font-mono text-gray-900">
                            {provider.identifier || "-"}
                          </td>
                          <td className="px-2 py-0.5 text-gray-700">
                            {formatEntityName(provider)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DetailSection>
              )}

              {hasLineDrg && lineDrg && (
                <DetailSection
                  title="DRG"
                  id={`claim-drg-section-line-${index}`}
                >
                  <div className="text-sm text-gray-700">
                    <span className="font-mono font-medium text-gray-900">
                      {lineDrg.code}
                    </span>
                    {lineDrg.subType && (
                      <span className="ml-2 text-gray-500">
                        ({lineDrg.subType})
                      </span>
                    )}
                    {lineDrg.description && (
                      <div className="mt-1 text-gray-600">
                        {lineDrg.description}
                      </div>
                    )}
                  </div>
                </DetailSection>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default ClaimServiceLineRow;
